import { useCallback, useEffect, useState } from 'react';

/** Small data-loading hook for admin screens. Pass `deps` to re-run when filters change. */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[] = []
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const guard = { active: true };
    setLoading(true);
    void load()
      .then((d) => {
        if (guard.active) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (guard.active) setError(e instanceof Error ? e.message : 'Something went wrong');
      })
      .finally(() => {
        if (guard.active) setLoading(false);
      });
    return () => {
      guard.active = false;
    };
  }, [tick, ...deps]);

  return { data, loading, error, reload };
}
